import { useEffect, useState, memo, useRef } from "react";

const LOADING_LOGO_SRC = "/lovable-uploads/c53fa286-eb95-47d0-bd04-e67a728fcf07.png";

const LoadingScreen = memo(() => {
  const [isLoading, setIsLoading] = useState(true);
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(0);
  const ringsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Optimized loading animation with easing
    const duration = 400;
    const startTime = performance.now();

    const easeOutExpo = (t: number) => t === 1 ? 1 : 1 - Math.pow(2, -10 * t);

    const animateProgress = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const linearProgress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutExpo(linearProgress) * 100;
      setProgress(easedProgress);

      if (elapsed < duration) {
        requestAnimationFrame(animateProgress);
      } else {
        setProgress(100);
        setIsExiting(true);
        setTimeout(() => setIsLoading(false), 350);
      }
    };

    requestAnimationFrame(animateProgress);
  }, []);

  if (!isLoading) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-background"
      style={{
        opacity: isExiting ? 0 : 1,
        transform: isExiting ? 'scale(1.02)' : 'scale(1)',
        transition: 'opacity 0.35s cubic-bezier(0.16, 1, 0.3, 1), transform 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
        pointerEvents: isExiting ? 'none' : 'auto',
      }}
    >
      {/* Animated gradient background */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse at 50% 30%, hsl(var(--gold) / 0.08) 0%, transparent 50%),
            radial-gradient(ellipse at 20% 80%, hsl(var(--deep-red) / 0.04) 0%, transparent 40%),
            radial-gradient(ellipse at 80% 70%, hsl(var(--gold) / 0.05) 0%, transparent 40%)
          `,
          animation: 'bgPulse 4s ease-in-out infinite',
        }}
      />

      {/* Central content */}
      <div className="relative flex flex-col items-center z-10">
        {/* Animated rings */}
        <div ref={ringsRef} className="absolute inset-0 flex items-center justify-center pointer-events-none">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full border border-gold/10"
              style={{
                width: `${120 + i * 40}px`,
                height: `${120 + i * 40}px`,
                animation: `ringExpand ${2 + i * 0.5}s ease-out infinite`,
                animationDelay: `${i * 0.3}s`,
              }}
            />
          ))}
        </div>

        {/* Logo container */}
        <div className="relative mb-8">
          {/* Outer glow */}
          <div 
            className="absolute -inset-10 rounded-full"
            style={{
              background: 'radial-gradient(circle, hsl(var(--gold) / 0.2) 0%, transparent 60%)',
              animation: 'pulseGlow 2s ease-in-out infinite',
            }}
          />
          
          {/* Inner glow */}
          <div 
            className="absolute -inset-4 rounded-full"
            style={{
              background: 'radial-gradient(circle, hsl(var(--gold) / 0.1) 0%, transparent 80%)',
              animation: 'pulseGlow 2s ease-in-out infinite 0.5s',
            }}
          />

          {/* Logo */}
          <div className="relative w-32 h-32 md:w-40 md:h-40 flex items-center justify-center">
            <img
              src={LOADING_LOGO_SRC}
              alt="Jazz Insights"
              className="w-24 h-24 md:w-32 md:h-32 object-contain"
              style={{
                animation: 'logoBreathe 2.5s ease-in-out infinite',
                filter: 'drop-shadow(0 0 20px hsl(var(--gold) / 0.4))',
              }}
            />
          </div>
        </div>

        {/* Title with shimmer effect */}
        <div className="relative overflow-hidden mb-1">
          <h1 className="font-display text-xl md:text-2xl tracking-[0.3em] text-gold/90 font-light">
            JAZZ INSIGHTS
          </h1>
          <div 
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(90deg, transparent, hsl(var(--gold) / 0.3), transparent)',
              animation: 'shimmer 3s ease-in-out infinite',
            }}
          />
        </div>

        {/* Tagline */}
        <p 
          className="text-gold/50 text-xs tracking-[0.4em] uppercase font-light mb-6"
          style={{
            animation: 'fadeInUp 0.8s ease-out 0.2s both',
          }}
        >
          Where Jazz Lives
        </p>

        {/* Premium progress bar */}
        <div className="relative w-40 md:w-52">
          {/* Track */}
          <div className="h-[2px] bg-gold/10 rounded-full overflow-hidden">
            {/* Fill with glow */}
            <div 
              className="h-full rounded-full relative"
              style={{ 
                width: `${progress}%`,
                background: 'linear-gradient(90deg, hsl(var(--gold) / 0.4), hsl(var(--gold) / 0.8), hsl(var(--gold) / 0.6))',
                boxShadow: '0 0 10px hsl(var(--gold) / 0.5), 0 0 20px hsl(var(--gold) / 0.3)',
                transition: 'width 0.08s linear',
              }}
            />
          </div>
          
          {/* Progress indicator dot */}
          <div 
            className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-gold"
            style={{
              left: `calc(${progress}% - 4px)`,
              boxShadow: '0 0 8px hsl(var(--gold) / 0.8)',
              transition: 'left 0.08s linear',
            }}
          />
        </div>
      </div>

      <style>{`
        @keyframes logoBreathe {
          0%, 100% { transform: scale(1) rotate(0deg); }
          50% { transform: scale(1.03) rotate(1deg); }
        }
        
        @keyframes pulseGlow {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.05); }
        }
        
        @keyframes ringExpand {
          0% { transform: scale(0.8); opacity: 0.3; }
          50% { opacity: 0.15; }
          100% { transform: scale(1.3); opacity: 0; }
        }
        
        @keyframes bgPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          50%, 100% { transform: translateX(100%); }
        }
        
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
});

LoadingScreen.displayName = 'LoadingScreen';

export default LoadingScreen;
